/**
 * Moya Fit — Brand Theme System
 * ——————————————————————————————
 * 8 selectable tenant themes. Only the primary brand color changes —
 * all other app colors (icon grid, charts, etc) remain untouched.
 */

export interface BrandTheme {
  id: 'bordo' | 'lacivert' | 'orman' | 'kömür' | 'sarı' | 'pembe' | 'turuncu' | 'mor';
  label: string;          // Turkish name shown in UI
  primary: string;        // main brand color
  primaryHover: string;   // +10% luminance / darker shade
  primarySoft: string;    // very light tint for active/hover backgrounds
  primaryTint: string;    // slightly darker tint for badges
}

export const BRAND_THEMES: Record<string, BrandTheme> = {
  bordo: {
    id: 'bordo',
    label: 'Bordo (Klasik)',
    primary: '#6b0f1a',
    primaryHover: '#8a1422',
    primarySoft: '#fdf2f3',
    primaryTint: '#fbeff1',
  },
  lacivert: {
    id: 'lacivert',
    label: 'Lacivert',
    primary: '#1e3a8a',
    primaryHover: '#2749b5',
    primarySoft: '#eef2ff',
    primaryTint: '#e0e7ff',
  },
  orman: {
    id: 'orman',
    label: 'Orman Yeşili',
    primary: '#065f46',
    primaryHover: '#087861',
    primarySoft: '#ecfdf5',
    primaryTint: '#d1fae5',
  },
  'kömür': {
    id: 'kömür',
    label: 'Kömür (Antrasit)',
    primary: '#1f2937',
    primaryHover: '#374151',
    primarySoft: '#f3f4f6',
    primaryTint: '#e5e7eb',
  },
  'sarı': {
    id: 'sarı',
    label: 'Sarı (Bal)',
    primary: '#ca8a04',
    primaryHover: '#eab308',
    primarySoft: '#fefce8',
    primaryTint: '#fef3c7',
  },
  pembe: {
    id: 'pembe',
    label: 'Pembe',
    primary: '#be185d',
    primaryHover: '#db2777',
    primarySoft: '#fdf2f8',
    primaryTint: '#fce7f3',
  },
  turuncu: {
    id: 'turuncu',
    label: 'Turuncu',
    primary: '#c2410c',
    primaryHover: '#ea580c',
    primarySoft: '#fff7ed',
    primaryTint: '#ffedd5',
  },
  mor: {
    id: 'mor',
    label: 'Mor',
    primary: '#6d28d9',
    primaryHover: '#7c3aed',
    primarySoft: '#f5f3ff',
    primaryTint: '#ede9fe',
  },
};

export const DEFAULT_THEME_ID: BrandTheme['id'] = 'bordo';

/** Apply a theme's colors as CSS custom properties on <html>. */
export function applyBrandTheme(themeId: string | null | undefined): void {
  const theme = BRAND_THEMES[themeId || ''] || BRAND_THEMES[DEFAULT_THEME_ID];
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', theme.primary);
  root.style.setProperty('--brand-primary-hover', theme.primaryHover);
  root.style.setProperty('--brand-primary-soft', theme.primarySoft);
  root.style.setProperty('--brand-primary-tint', theme.primaryTint);
  // Save to localStorage so next paint doesn't flash the default color.
  try { localStorage.setItem('moya_brand_theme', theme.id); } catch { /* ignore */ }
}

/** Restore theme from localStorage synchronously before React renders. */
export function restoreBrandThemeFromStorage(): void {
  try {
    const saved = localStorage.getItem('moya_brand_theme');
    if (saved && BRAND_THEMES[saved]) applyBrandTheme(saved);
  } catch {
    /* ignore */
  }
}
